import { IComponent } from "@faire/web-api/indigofair/data/IComponent";
import { useCallback, useEffect, useRef, useState } from "react";

import { setTimeoutIfPossible } from "@faire/retailer/lib/async-functions/setTimeoutIfPossible";
import { circularMod } from "@faire/retailer/lib/math";

const DEFAULT_COMPONENT_ROTATION_MS = 7500;

export const useAnimatedRotation = (
  components: IComponent[] | undefined,
  rotationMs: number | undefined
) => {
  const rotationDelayMs = rotationMs ?? DEFAULT_COMPONENT_ROTATION_MS;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const fadeInInterval = useRef<number>(0);
  const fadeOutInterval = useRef<number>(0);

  const pauseScroll = useCallback(() => {
    clearTimeout(fadeOutInterval.current);
    clearTimeout(fadeInInterval.current);
  }, [fadeOutInterval, fadeInInterval]);

  const resumeScroll = useCallback(() => {
    if (!components || components?.length <= 1) {
      return;
    }

    // Make sure there's always only one set of timeouts
    pauseScroll();

    fadeOutInterval.current =
      setTimeoutIfPossible(() => {
        setIsVisible(false);
      }, rotationDelayMs) ?? 0;

    fadeInInterval.current =
      setTimeoutIfPossible(() => {
        setCurrentIndex((currentIndex) =>
          circularMod(currentIndex + 1, components.length)
        );
        setIsVisible(true);
        resumeScroll();
      }, rotationDelayMs + 500) ?? 0;
  }, [components, pauseScroll, rotationDelayMs]);

  useEffect(() => {
    resumeScroll();
    return () => {
      pauseScroll(); // cleaning timeouts
    };
  }, [resumeScroll, pauseScroll]);

  return {
    isVisible,
    resumeScroll,
    pauseScroll,
    currentIndex,
  };
};
